import { askChatbot, sendFeedback } from '../../api/ChatbotApi';

/**
 * The conversation, held outside the widget.
 *
 * MainRoutes swaps its MainLayout while a route's data loads (and for the
 * NotFound / NotAccess branches), which unmounts the widget. Component state
 * went with it: the conversation vanished, and an answer in flight was cut
 * off. Kept here, both survive; the widget only renders what is in the store.
 *
 * A context provider would have to sit above MainRoutes -- the very thing that
 * remounts -- so this is a plain module instead.
 *
 * Still in memory only. A reload starts over, as before.
 */

const empty = (owner) => ({
    owner,
    messages: [],
    sessionId: undefined,
    isAnswering: false,
    // The last status frame from the API; the widget turns it into a label.
    statusFrame: null
});

let state = empty(null);
let controller = null;
const listeners = new Set();

const emit = () => listeners.forEach((listener) => listener(state));

const update = (patch) => {
    state = { ...state, ...patch };
    emit();
};

export const getChatState = () => state;

export const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

/**
 * Starts over when someone else is signed in. Logging out does not reload the
 * page, so without this the next customer on the same browser would open the
 * widget onto the previous one's conversation.
 */
export function claimConversation() {
    const owner = localStorage.getItem('token');
    if (state.owner === owner) return;
    controller?.abort();
    controller = null;
    state = empty(owner);
    emit();
}

// The bot message is always the last entry, so every frame updates it in
// place rather than appending.
const updateAnswer = (patch) => {
    const messages = [...state.messages];
    const last = messages[messages.length - 1];
    messages[messages.length - 1] = { ...last, ...patch(last) };
    update({ messages });
};

/**
 * A failure is recorded on the answer, never by overwriting it. Text that has
 * already streamed stays visible, with the error shown after it -- and the
 * turn is marked failed either way, so it is not sent back as history.
 */
const fail = (error) => updateAnswer(() => ({ failed: true, error }));

/**
 * 429 is the API's throttle (PPS-345). 401 / 403 come from its authorizer
 * (PPS-348): the portal token is missing, expired, or logged out, and only
 * logging in again fixes that -- retrying does not.
 */
const errorKind = (status) => {
    if (status === 429) return 'busy';
    if (status === 401 || status === 403) return 'expired';
    return 'generic';
};

/**
 * A stream that stops sending without closing would leave the widget answering
 * forever -- and the store outlives page changes, so only a reload would clear
 * it. Frames normally arrive well under a second apart (status, then text);
 * this long without one means the connection is stuck.
 */
const IDLE_TIMEOUT_MS = 30000;

export async function sendQuestion(text) {
    // The token may have changed while the widget stayed mounted.
    claimConversation();
    if (!text || state.isAnswering) return;

    // The conversation so far, sent with the question. The API keeps nothing,
    // so this is the whole of its context. Failed turns are left out: half an
    // answer, or an error message, is not something the model said.
    const history = state.messages
        .filter((message) => message.text && !message.failed)
        .slice(-6)
        .map((message) => ({ role: message.role === 'bot' ? 'assistant' : 'user', text: message.text }));

    update({
        isAnswering: true,
        statusFrame: null,
        messages: [...state.messages, { role: 'user', text }, { role: 'bot', text: '', sources: [] }]
    });

    const owner = state.owner;
    controller = new AbortController();
    const signal = controller.signal;
    // A conversation reset mid-answer (claimConversation) aborts this request;
    // nothing below may write into the new conversation.
    const current = () => !signal.aborted && state.owner === owner;

    let timedOut = false;
    let idleTimer;
    const resetIdleTimer = () => {
        clearTimeout(idleTimer);
        idleTimer = setTimeout(() => {
            timedOut = true;
            controller?.abort();
        }, IDLE_TIMEOUT_MS);
    };
    resetIdleTimer();

    try {
        await askChatbot({
            question: text,
            history,
            sessionId: state.sessionId,
            signal,
            onFrame: (frame) => {
                if (!current()) return;
                resetIdleTimer();
                if (frame.type === 'status') {
                    update({ statusFrame: frame });
                } else if (frame.type === 'text') {
                    // The model often opens with a blank line. Dropped here
                    // rather than at render, because an empty text is also what
                    // tells the status line to stay up.
                    updateAnswer((answer) => ({
                        text: answer.text ? answer.text + frame.text : frame.text.trimStart()
                    }));
                } else if (frame.type === 'sources') {
                    updateAnswer(() => ({ sources: frame.sources ?? [] }));
                } else if (frame.type === 'done') {
                    // What a rating of this answer refers to, kept on the
                    // answer so each rating is self-contained.
                    updateAnswer(() => ({ answerId: frame.answerId, sessionId: frame.sessionId }));
                    update({ sessionId: frame.sessionId });
                } else if (frame.type === 'error') {
                    // Delivered in-band: by now the response has usually
                    // started, so the status code is long gone.
                    fail('generic');
                }
            }
        });
    } catch (error) {
        if (timedOut && state.owner === owner) {
            fail('generic');
        } else if (error.name !== 'AbortError' && current()) {
            fail(errorKind(error.status));
        }
    } finally {
        clearTimeout(idleTimer);
        if (state.owner === owner) update({ isAnswering: false });
        if (controller?.signal === signal) controller = null;
    }
}

/**
 * Records the customer's rating of one answer and sends it (PPS-390). Called
 * again to change it or to add a reason; the API keeps the latest. The rating
 * shows at once and is not undone if sending fails.
 */
export function rateAnswer(index, rating, reason) {
    // Another customer may have signed in while the widget stayed open; their
    // conversation starts empty, so nothing here is rated under their token.
    claimConversation();
    const message = state.messages[index];
    if (!message?.answerId || message.failed) return;
    const messages = [...state.messages];
    messages[index] = { ...message, feedback: { rating, reason } };
    update({ messages });
    sendFeedback({ sessionId: message.sessionId, answerId: message.answerId, rating, reason });
}
